<template>
  <div class="date-picker__wrapper">
    <slot
      v-if="useCustomInput"
      name="datePickerInputContainer"
      :set-show-date-picker="value => showDatePicker = value"
      :reset-selected-date="() => resetSelectedDate()"
      :trigger-id="'datePickerInputId'"
      :ev-blur="e => {
        if (!isSameWrapperOnBlur(e)) {
          showDatePicker = false;
        }
      }"
    />
    <m-date-picker-input
      v-else
      ref="datePickerInput"
      :selected-date="selectedDate"
      :selected-month="selectedMonth"
      :selected-year="selectedYear"
      :language="language"
      :date-format="dateFormat"
      :state="state"
      :error-text="errorText"
      :hint-text="hintText"
      :disabled="getDisabled"
      :label-text="labelText"
      :placeholder="placeholder"
      :clearable="clearable"
      @show-date-picker="showDatePicker = true"
      @hide-date-picker="showDatePicker = false"
      @reset-selected-date="resetSelectedDate"
    >
      <template #datePickerInputSlot="params">
        <slot name="datePickerInputCustom" :params="params" />
      </template>
    </m-date-picker-input>
    <div
      v-if="showDatePicker && !getDisabled"
      ref="datePickerContainer"
      class="datepicker--no-outline"
    >
      <m-date-picker-ranged-container
        :previous-date-list="previousDateList"
        :next-date-list="nextDateList"
        :current-date-list="currentDateList"
        :picker-date="pickerDate"
        :picker-month="pickerMonth"
        :picker-year="pickerYear"
        :selected-date="selectedDate"
        :selected-month="selectedMonth"
        :selected-year="selectedYear"
        :disabled-dates="disabledDates"
        :language="language"
        class="position-relative datepicker--no-outline"
        tabindex="0"
        @update-selected-date="updateSelectedDate"
        @update-month="updateMonth"
        @hide-date-picker="showDatePicker = false"
      />
    </div>
  </div>
</template>

<script>
import MDatePickerRangedContainer from './components/DatePickerRangedContainer';
import MDatePickerMixins from '../DatePicker/mixins';
import MDatePickerInput from '../DatePicker/components/DatePickerInput';
import { createPopper } from '@popperjs/core';

export default {
  name: 'MDatePicker',
  components: {
    MDatePickerInput,
    MDatePickerRangedContainer,
  },
  mixins: [
    MDatePickerMixins,
  ],
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    language: {
      type: String,
      default: 'en',
    },
    dateFormat: {
      type: String,
      default: 'DD MMM YYYY',
    },
    state: {
      type: Boolean,
      default: null,
    },
    errorText: {
      type: String,
      default: '',
    },
    hintText: {
      type: String,
      default: '',
    },
    clickableReadonly: {
      type: Boolean,
      default: true,
    },
    disabled: {
      type: Boolean,
      default: null,
    },
    labelText: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    placement: {
      type: String,
      default: 'right',
    },
    offsetY: {
      type: Number,
      default: 4,
    },
    offsetX: {
      type: Number,
      default: 0,
    },
    disablePastDates: {
      type: Boolean,
      default: true,
    },
    clearable: {
      type: Boolean,
      default: true,
    },
    useCustomInput: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    showDatePicker: false,
    popperInstance: null,
    previousDateList: {},
    nextDateList: {},
    currentDateList: {},
    pickerDate: [],
    pickerMonth: [],
    pickerYear: [],
    selectedDate: [],
    selectedMonth: [],
    selectedYear: [],
    disabledDates: null,
  }),
  watch: {
    disabled() {
      this.showDatePicker = false;
    },
    clickableReadonly() {
      this.showDatePicker = false;
    },
    showDatePicker(val) {
      this.$nextTick(() => {
        if (val) {
          if (!this.getDisabled) {
            this.resetPopperInstance();
          }
        } else {
          this.initPickerDate();
          this.initDateList();
        }
        this.initDisabledDates();
      });
    },
    value() {
      this.initSelectedDate();
      this.initPickerDate();
      this.initDateList();
    },
  },
  computed: {
    getDisabled() {
      if (this.disabled !== null) {
        return this.disabled;
      }
      return !this.clickableReadonly;
    },
  },
  mounted() {
    this.initSelectedDate();
    this.initPickerDate();
    this.initDateList();
    this.initDisabledDates();
  },
  methods: {
    updateMonth(newOffset) {
      for (let index = 0; index < 2; index += 1) {
        const date = new Date(this.pickerYear[index], this.pickerMonth[index] + newOffset);
        this.$set(this.pickerMonth, index, date.getMonth());
        this.$set(this.pickerYear, index, date.getFullYear());
      }
      this.initDateList();
    },
    updateSelectedDate(val) {
      const { date, month, year } = val;
      let index = 0;
      if (this.selectedDate[0]) {
        index = 1;
      }
      if (this.selectedDate[1]) {
        index = 0;
        this.setSelectedDate(1, null);
      }
      this.setSelectedDate(index, new Date(
        year,
        month,
        date,
      ));
      const selectedStartDate = new Date(this.selectedYear[0], this.selectedMonth[0], this.selectedDate[0]).getTime();
      const selectedEndDate = this.selectedYear[1]
        ? new Date(this.selectedYear[1], this.selectedMonth[1], this.selectedDate[1]).getTime()
        : new Date(this.selectedYear[0], this.selectedMonth[0], this.selectedDate[0]).getTime();
      if (selectedStartDate > selectedEndDate) {
        [this.selectedDate[0], this.selectedDate[1]] = [this.selectedDate[1], this.selectedDate[0]];
        [this.selectedMonth[0], this.selectedMonth[1]] = [this.selectedMonth[1], this.selectedMonth[0]];
        [this.selectedYear[0], this.selectedYear[1]] = [this.selectedYear[1], this.selectedYear[0]];
      }
      const output = [];
      for (let currentIndex = 0; currentIndex < 2; currentIndex += 1) {
        if (this.selectedYear[currentIndex]) {
          output.push(new Date(
            this.selectedYear[currentIndex],
            this.selectedMonth[currentIndex],
            this.selectedDate[currentIndex],
          ));
        } else {
          output.push(null);
        }
      }
      this.$emit('input', output);
      this.initDateList();
    },
    setSelectedDate(index, date) {
      if (date) {
        this.$set(this.selectedDate, index, date.getDate());
        this.$set(this.selectedMonth, index, date.getMonth());
        this.$set(this.selectedYear, index, date.getFullYear());
      } else {
        this.$set(this.selectedDate, index, null);
        this.$set(this.selectedMonth, index, null);
        this.$set(this.selectedYear, index, null);
      }
    },
    resetSelectedDate() {
      this.setSelectedDate(0, null);
      this.setSelectedDate(1, null);
      this.showDatePicker = false;
      this.initPickerDate();
      this.$emit('input', []);
    },
    initSelectedDate() {
      if (this.value !== null && this.value.length > 1) {
        this.value.forEach((date, index) => {
          const outputDate = date !== null ? new Date(date) : date;
          this.setSelectedDate(index, outputDate);
        });
      } else {
        this.setSelectedDate(0, null);
        this.setSelectedDate(1, null);
      }
    },
    initPickerDate() {
      const today = new Date();
      const initYear = this.selectedYear[0] !== null ? this.selectedYear[0] : today.getFullYear();
      const initMonth = this.selectedMonth[0] !== null ? this.selectedMonth[0] : today.getMonth();
      const initDate = this.selectedDate[0] !== null ? this.selectedDate[0] : today.getDate();

      for (let index = 0; index < 2; index += 1) {
        const adjustedDate = new Date(initYear, initMonth + index, initDate);
        this.$set(this.pickerYear, index, adjustedDate.getFullYear());
        this.$set(this.pickerMonth, index, adjustedDate.getMonth());
        this.$set(this.pickerDate, index, adjustedDate.getDate());
      }
    },
    initDateList() {
      this.previousDateList = {};
      this.nextDateList = {};
      this.currentDateList = {};

      for (let index = 0; index < 2; index += 1) {
        const firstDate = new Date(this.pickerYear[index], this.pickerMonth[index]);
        const lastDate = new Date(this.pickerYear[index], this.pickerMonth[index] + 1, 0);

        const firstDateDay = firstDate.getDay();
        const lastDateDay = lastDate.getDay();

        // Generate list of date from previous month
        const previousMonthDuration = new Date(this.pickerYear[index], this.pickerMonth[index], 0).getDate();
        this.previousDateList[index] = [];
        for (let i = firstDateDay - 1; i >= 0; i -= 1) {
          this.previousDateList[index].push(previousMonthDuration - i);
        }

        // Generate list of date from next month
        this.nextDateList[index] = [];
        const lastDayOnWeek = 6;
        for (let j = 1; j <= lastDayOnWeek - lastDateDay; j += 1) {
          this.nextDateList[index].push(j);
        }

        // Genrate list of date for current month
        this.currentDateList[index] = Array.from({ length: lastDate.getDate() }, (v, k) => k + 1);
      }
    },
    resetPopperInstance() {
      if (this.popperInstance) {
        this.popperInstance.destroy();
        this.popperInstance = null;
      }
      const popperPlacement = {
        left: 'bottom-start',
        right: 'bottom-end',
      };
      const datePickerDistance = this.offsetY;
      const datePickerSkid = this.offsetX;
      const triggerEl = this.$refs.datePickerInput ? this.$refs.datePickerInput.$el : this.$el.querySelector('#datePickerInputId');
      const datePickerContainerEl = this.$refs.datePickerContainer;
      this.popperInstance = createPopper(triggerEl, datePickerContainerEl, {
        placement: popperPlacement[this.placement],
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [datePickerSkid, datePickerDistance],
            },
          },
          {
            name: 'flip',
          },
        ],
      });
    },
    initDisabledDates() {
      if (this.disablePastDates) {
        this.disabledDates = { to: new Date().setHours(0,0,0,0) };
      }
      else this.disabledDates = null;
    },
  },
};
</script>
